import { PhoneWithCountryCode } from '@/types/phone';
import { ApiPaginatedResponse, ApiResponse } from './api';
import { PoaStatus } from './company';
import { DocumentStatus, DocumentTypes } from './document';

export const TEAM_TABLE_FILTER_KEY = 'team_table_filter';

interface BankData {
  iban: string;
  bic: string;
}

interface Moss {
  is_moss: boolean;
  is_registered: boolean;
  bank_data: BankData;
  registered_countries: string[];
  vats: string[];
  products: string[];
}

interface Ioss {
  registered_countries: string[];
}

interface Balance {
  current: number;
  total_earned: number;
}

interface Setting {
  key: string;
  value: string;
}

export interface StatusChange {
  status: string;
  date: string;
  admin_user?: string;
}

interface ChargebackPenaltyHistory {
  _id: string;
  type: string;
  amount: number;
  date: string;
}

export interface DropboxEmail {
  purchases_invoices: Array<string> | string;
  sales_invoices: Array<string> | string;
}

export enum CompanyType {
  FREELANCE = 'freelance',
  VIRTUAL = 'virtual',
  PRO = 'remote',
  PREMIUM = 'crypto',
  ULTRA = 'ultra'
}

export interface AdminUser {
  _id: string;
  first_name: string;
  last_name: string;
  avatar: string;
}

interface CompanyPendingPlanChange {
  change_type: 'downgrade' | 'upgrade';
  current_plan: CompanyType;
  effective_date: string;
  requested_date: string;
  requested_plan: CompanyType;
}

export interface Company {
  _id: string;
  moss: Moss;
  ioss: Ioss;
  balance: Balance;
  company_type: CompanyType;
  users: string[];
  banks: string[];
  name: string;
  description: string;
  registry_number: string;
  vat_number: string;
  vat_date: string | null;
  representative: string;
  status: string;
  email: string;
  phone: PhoneWithCountryCode;
  website: string;
  settings: Setting[];
  created: string;
  status_changes: StatusChange[];
  __v: number;
  last_vat: string;
  reference_number: string;
  last_oss: string;
  last_ioss: string;
  date_accounting: string;
  last_tsd: string;
  last_accountancy: string;
  last_oss_quarter: string;
  date_registered: string;
  accounting_credentials: {
    api_id: string;
    api_key: string;
  };
  churn_provider: string;
  churn_reason: string;
  off_board_requested: boolean;
  cs_agent: string;
  junior_accountant: string;
  avatar: string;
  senior_accountant: string;
  support_pin: number;
  chargeback_penalty: number;
  chargeback_penalty_history: ChargebackPenaltyHistory[];
  other_vats?: {
    country: string;
    vat: string;
  }[];
  address_city: string;
  address_street: string;
  address_zip: string;
  address_country: string;
  email_dropbox_addresses: DropboxEmail;
  share_capital: {
    value: number;
    registered: string;
  };
  accounting_poa?: {
    verified_by: AdminUser;
    date_verified: string;
    date_requested: string;
    date_granted: string;
    status: PoaStatus;
  };
  annual_report_poa?: {
    verified_by: AdminUser;
    date_verified: string;
    date_requested: string;
    date_granted: string;
    status: PoaStatus;
  };
  stats_poa?: {
    verified_by: AdminUser;
    date_verified: string;
    date_requested: string;
    date_granted: string;
    status: PoaStatus;
  };
  members?: {
    first_name: string;
    last_name: string;
    email: string;
    eresident_id: string;
    certificate_valid_until: string;
  }[];
  update_phone: {
    phone: PhoneWithCountryCode;
    verification_code: string;
    requested: string;
  };
  has_verified_phone?: boolean;
  terms_and_conditions?: boolean;
  last_plan_change?: string;
  pending_plan_change?: CompanyPendingPlanChange;
  blacklisted_payment_methods?: string[];
  country_code: string;
}

export enum UserRole {
  ADMIN = 'admin',
  REPRESENTATIVE = 'representative',
  ACCOUNTANT = 'accountant',
  HUMAN_RESOURCES = 'human_resources',
  SECRETARY = 'secretary'
}

export type UserLocale = 'en_US' | 'es_ES';

export type User = {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar?: string;
  created: string;
  locale: UserLocale;
  phone: PhoneWithCountryCode;
  promo_code?: string | null;
  referrer_company?: string | null;
  role: UserRole;
  company: Company;
  address_city?: string;
  address_country?: string;
  address_street?: string;
  address_zip?: string;
  permissions: string[];
  update_phone: {
    phone: string;
    verification_code: string;
    requested: string;
  };
  eresident_id?: string;
  first_access?: boolean;
  annual_report_first_access?: string;
  certificate?: {
    start_date: string;
    end_date: string;
  };
};

export interface Passport {
  _id: string;
  rejection: string[];
  out_of_pocket: boolean;
  name: string;
  company: string;
  path: string;
  type: string;
  mime_type: string;
  description: string;
  invoice_from_us: boolean;
  status: string;
  created: string;
  original_date: string;
  uploaded_by_user: string;
}

export type Member = {
  address: string;
  address_city: string;
  address_country: string;
  address_street: string;
  address_zip: string;
  birthdate: string;
  company: string;
  created: string;
  first_name: string;
  last_name: string;
  email: string;
  is_representative: boolean;
  locale: string;
  phone?: PhoneWithCountryCode;
  politically_exposed: boolean;
  registration: string;
  registration_id: string;
  eresident_id: string;
  representative: string;
  shareholder_contribution: number;
  signature_valid: string;
  type: string[];
  _id: string;
  passport: Passport;
  avatar: string;
  certificate?: {
    // signing certificate
    text: string;
    properties: {
      issuer: string;
      subject: string;
      serialNumber: string;
      CN: string;
      attributes: string;
      validFrom: string;
      validTo: string;
    };
    signature_algorithm: string;
    start_date: string;
    end_date: string;
  };
  update_phone: {
    phone: string;
    verification_code: string;
    requested: string;
  };
};

export enum NotificationType {
  PAYMENT = 'payment',
  PAYMENT_FAILED = 'failed_payment',
  INVOICE_MISSING = 'invoice_missing',
  INVOICE_UNCLEAR = 'invoice_unclear',
  TRANSFER_MISSING = 'transfer_missing',
  TRANSFER_UNCLEAR = 'transfer_unclear',
  TRANSFERS_MISSING = 'transfers_missing',
  REPORT_READY = 'report_ready',
  LINK_BANK_ACCOUNT = 'link_bank_account',
  UNMATCHED_MOVEMENTS = 'unmatched_movements',
  UPLOAD_STATEMENTS = 'upload_statements',
  UPLOAD_INVOICES = 'upload_invoices',
  SALARIES_READY = 'salaries_ready',
  PENDING_PACKAGE = 'pending_package',
  TICKET_ANSWERED = 'ticket_answered',
  FEATURE_ANSWERED = 'feature_answered',
  ACCOUNTING_REQUIREMENT_DENIED = 'accounting_requirement_denied',
  ACCOUNTING_REQUIREMENT_REQUESTED = 'accounting_requirement_requested',
  ACCOUNTING_INITIAL_REQUIREMENTS = 'accounting_initial_requirements',
  SALES_INVOICE_AUTOMATION_FAILED = 'sales_invoice_automation_failed'
}

enum TicketType {
  CONTACT = 'contact',
  SUPPORT = 'support',
  AUTOMATIC = 'automatic'
}

export interface Notification {
  admin_user: string | null;
  user: string;
  link: string;
  metadata?: {
    customer?: string;
    service?: string;
    amount?: number;
    client_secret?: string;
    company?: string;
    description?: string;
    sequence_type?: string;
    type: DocumentTypes;
    text?: string;
    from?: string;
    ticket?: string;
    ticket_number?: number;
    ticket_type?: TicketType;
    user?: string;
    // report document
    out_of_pocket: boolean;
    _id: string;
    name: string;
    path: string;
    mime_type: string;
    sha256: string;
    invoice_from_us: boolean;
    status: DocumentStatus;
    created: string;
    original_date: string;
    uploaded_by_admin: string;
  };
  type: NotificationType;
  created: string;
  read: string | null;
  archived_at?: string | null;
  __v: number;
  _id: string;
}

export type GetMeResponse = ApiResponse<{
  user: User;
}>;

export type GetUserResponse = ApiResponse<{
  user: User;
}>;

export type GetNotificationsResponse = ApiResponse<{
  notifications: Notification[];
}>;

export type GetPaginatedUsersResponse = ApiPaginatedResponse<{
  docs: User[];
}>;

export type UpdateUserData = Partial<
  Pick<
    User,
    | 'first_name'
    | 'last_name'
    | 'phone'
    | 'email'
    | 'address_street'
    | 'address_city'
    | 'address_zip'
    | 'address_country'
    | 'locale'
    | 'role'
  >
> & {
  force_update?: boolean;
};

export type CreateUserData = Pick<
  User,
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone'
  | 'address_street'
  | 'address_city'
  | 'address_zip'
  | 'address_country'
  | 'locale'
  | 'role'
>;

export interface ReferralBalance {
  current: number;
  total_earned: number;
}

export enum ReferralTransactionType {
  RECEIVED_DISCOUNT = 'received_discount',
  ACCOUNTANCY_DISCOUNT = 'discount_applied_in_accountancy'
}

export interface Referral {
  _id: string;
  created: string;
  email: string;
  amount: number;
  transaction_type: ReferralTransactionType;
}

export type UpdateUserProfileResponse = ApiResponse<{
  user: User;
}>;

export type CreateUserProfileResponse = ApiResponse<{
  user: User;
  company: Company;
}>;

export type UpdateUserAvatarResponse = ApiResponse<{
  user: User;
}>;

export type UpdateCompanySettingsResponse = ApiResponse<{
  company: Company;
}>;

export type GetCompanyReferralDataResponse = ApiResponse<{
  referral_link: string;
  _id: string;
  balance: ReferralBalance;
}>;

export type GetCompanyReferralHistoryResponse = ApiResponse<{
  balance: ReferralBalance;
  referrals_history: Omit<
    ApiPaginatedResponse<{
      docs: Referral[];
    }>,
    'error' | 'error_code' | 'isError' | 'message'
  >;
}>;

export type updateFirstAccessRequest = {
  first_access: boolean;
};

export interface EResidencyReminder {
  isExpired: boolean;
  isCloseToExpire: boolean;
}

export type MemberWithCertificateExpirationStatus = Member & {
  isExpired?: boolean;
  isCloseToExpire?: boolean;
};

export type GetCertificateReminderResponse = ApiResponse<{
  company_members: MemberWithCertificateExpirationStatus[];
}>;

export enum UserPermissions {
  SEE_USERS = 'see_users',
  ADMIN_USERS = 'admin_users',

  SEE_TICKETS = 'see_tickets',
  ADMIN_TICKETS = 'admin_tickets',

  SEE_CUSTOMERS = 'see_customers',
  ADMIN_CUSTOMERS = 'admin_customers',

  SEE_DOCUMENTS = 'see_documents',
  ADMIN_DOCUMENTS = 'admin_documents',

  SEE_BANKS = 'see_banks',
  ADMIN_BANKS = 'admin_banks',

  SEE_BANK_MOVEMENTS = 'see_bank_movements',
  ADMIN_BANK_MOVEMENTS = 'admin_bank_movements',

  SEE_EMPLOYEES = 'see_employees',
  ADMIN_EMPLOYEES = 'admin_employees',

  SEE_PACKAGES = 'see_packages',
  ADMIN_PACKAGES = 'admin_packages',

  SEE_TRAVELS = 'see_travels',
  ADMIN_TRAVELS = 'admin_travels',

  SEE_PAYMENTS = 'see_payments',
  ADMIN_PAYMENTS = 'admin_payments'
}
